import type { ScheduledShow, Show } from '@shady-pines/db';
import type { DayName, Uuid } from '@shady-pines/types';
import { equal, isNonNil } from '@shady-pines/types';
import { useEffect, useMemo } from 'react';
import { useTrackStore } from '../track/track-store';
import { useProfilesByIds } from '../user/use-manage-user';
import { useUserStore } from '../user/user-store';
import { useShowStore } from './show-store';

export const useAllShows = () => {
  const shows = useShowStore((s) => s.shows);

  return useMemo(() => shows ?? [], [shows]);
};

export const useShowById = <I extends ScheduledShow['id'] | undefined>(id: I) => {
  const show = useShowStore(
    (store) => store.shows?.find((s) => s.id === id),
    (a, b) => equal(a, b),
  );

  return useMemo(() => show, [show]) as I extends ScheduledShow['id'] ? ScheduledShow : undefined;
};

export const useShowsOnDay = (day: DayName) => {
  const shows = useShowStore(
    (store) =>
      store.shows?.filter((s) => s.day_of_week === day).sort((a, b) => a.start_time - b.start_time),
    (a, b) => equal(a, b),
  );

  return useMemo(() => shows ?? [], [shows]);
};

export const useMyShows = () => {
  const userId = useUserStore((s) => s.me);

  const shows = useShowStore(
    (store) => {
      if (userId === '87eb56b5-670d-4d8f-836c-f51e693bd788') {
        return (store.shows ?? [])
          .filter((s) => s.user_id === userId && s.id === '4a1853c1-55a1-48ff-b8d0-d54ac29d0a57')
          .sort((a, b) => a.start_time - b.start_time);
      }

      return (store.shows ?? [])
        .filter((s) => s.user_id === userId)
        .sort((a, b) => a.start_time - b.start_time);
    },

    (a, b) => equal(a, b),
  );

  return useMemo(() => shows, [shows]);
};

export const useSubscribersToMyShow = (showId: Uuid<Show>) => {
  const fetching = useShowStore((s) => s.loading.fetchSubscribersToMyShow[showId]);
  const myShowSubscriptions = useShowStore((s) => s.myShowSubscriptions[showId], equal);
  const fetched = isNonNil(myShowSubscriptions);
  const fetchSubscribersToMyShow = useShowStore((s) => s.fetchSubscribersToMyShow);
  const { loading: profilesLoading, profiles } = useProfilesByIds(
    (myShowSubscriptions ?? []).map((s) => s.user_id),
    !fetched,
  );

  useEffect(() => {
    if (fetching || fetched) {
      return;
    }

    void fetchSubscribersToMyShow(showId);
  }, [fetchSubscribersToMyShow, fetched, fetching, showId]);

  return useMemo(
    () => ({
      loading: fetching || profilesLoading,
      myShowSubscriptions: myShowSubscriptions ?? [],
      profiles,
    }),
    [fetching, myShowSubscriptions, profiles, profilesLoading],
  );
};

export const useCurrentlyAiringShow = () => {
  const currentlyAiringShow = useTrackStore((s) => s.currentlyAiringShow);
  const currentlyPlayingShowContext = useTrackStore((s) => s.currentlyPlaying?.airing_show_context);
  const showDetails = useShowById(currentlyAiringShow ?? undefined);

  return useMemo(
    () => ({
      currentlyAiringShow: showDetails,
      showContext: currentlyPlayingShowContext,
    }),
    [currentlyPlayingShowContext, showDetails],
  );
};
